import { useCallback, useEffect, useState } from "react";
import moment from "moment/moment";
import { getIncidents, getIncidentSummary, resolveIncident, resendKakaoGift, resendFailedKakaoGifts, backfillKakaoGiftIncidents } from "../../api/system/incident.api";
import LoadingBlock from "../LoadingBlock";

/**
 * 운영 사고 현황 (시스템 관리자) — `incident_log`. 2026-10-30.
 *
 * 왜: 10-18 부터 12일간 카카오 선물 80건이 비즈캐시 잔액 부족으로 미발송된 채 아무도 몰랐다. 실패는 로그에만 있었고
 * 로그는 열어 봐야 보인다. 이 화면은 "지금 못 가고 있는 것" 을 한 곳에서, 열어 보지 않아도(탭 배지) 보이게 한다.
 *
 * 행 동작: KAKAO_GIFT 이력(refs.historyId 가 아니라 ref=benefit_result) → 채널 단위 일괄 재발송(dry-run 먼저) ·
 *          공통 '처리 완료'. 코드 소진(CODE_POOL)은 고객사가 코드를 적재해야 풀린다 — 여기서는 표시만.
 */
const SOURCES = [
  { label: "전체", value: "" },
  { label: "카카오 선물", value: "KAKAO_GIFT" },
  { label: "쿠폰 코드", value: "CODE_POOL" },
  { label: "발송 서버 거절", value: "NOTIFICATION_REJECT" },
  { label: "채널 종료 차단", value: "CHANNEL_INACTIVE" },
  { label: "예상 못 한 예외", value: "UNEXPECTED" },
];
const SEVERITY_CLASS = { ERROR: "bg-red-100 text-red-700", WARN: "bg-amber-100 text-amber-700", INFO: "bg-gray-100 text-gray-600" };
const PAGE_SIZE = 20;

const fmt = (iso) => (iso ? moment(iso).format("MM-DD HH:mm") : "-");

const IncidentAdmin = () => {
  const [filter, setFilter] = useState({ source: "", severity: "", channelId: "", unresolvedOnly: true, from: "", to: "" });
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [bulk, setBulk] = useState(null); // { channelId, result }
  const [toolChannel, setToolChannel] = useState("");
  const [backfillSince, setBackfillSince] = useState("2026-10-01");
  const [backfill, setBackfill] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError("");
    Promise.all([
      getIncidents({ ...filter, channelId: filter.channelId || undefined, page, size: PAGE_SIZE }),
      getIncidentSummary(),
    ]).then(([list, sum]) => { setData(list ?? { rows: [], total: 0 }); setSummary(sum ?? null); })
      .catch((e) => setError(e?.message || "조회 중 오류가 발생했습니다."))
      .finally(() => setLoading(false));
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async (row) => {
    const note = window.prompt("처리 메모(선택)", row.source === "KAKAO_GIFT" ? "재발송 완료" : "");
    if (note === null) return;
    setBusyId(row.id);
    try { await resolveIncident(row.id, note); load(); } finally { setBusyId(null); }
  };

  const onBulkResend = async (channelId, dryRun) => {
    if (!dryRun && !window.confirm(`채널 ${channelId} 의 실패 건을 전부 재발송합니다. 성공 건마다 크레딧이 차감됩니다. 비즈캐시는 충전했습니까?`)) return;
    setBusyId(`bulk-${channelId}`);
    try {
      const result = await resendFailedKakaoGifts(channelId, dryRun);
      setBulk({ channelId, result });
      if (!dryRun) load();
    } catch (e) { setError(e?.message || "일괄 재발송 실패"); }
    finally { setBusyId(null); }
  };

  const onResendOne = async (row) => {
    const historyId = row.refs?.historyId;
    if (!historyId) return;
    if (!window.confirm(`incident #${row.id} 의 선물을 재발송합니다(크레딧 차감).`)) return;
    setBusyId(row.id);
    try { const r = await resendKakaoGift(historyId); window.alert(r?.message ?? "완료"); load(); }
    finally { setBusyId(null); }
  };

  const onBackfill = async (dryRun) => {
    if (!backfillSince) return;
    if (!dryRun && !window.confirm(`${backfillSince} 이후의 카카오 선물 실패 이력을 사고 기록으로 가져옵니다. Slack 알림은 가지 않습니다.`)) return;
    setBusyId("backfill");
    try { const r = await backfillKakaoGiftIncidents(backfillSince, dryRun); setBackfill(r); if (!dryRun) load(); }
    catch (e) { setError(e?.message || "가져오기 실패"); }
    finally { setBusyId(null); }
  };

  const set = (k) => (e) => { setPage(0); setFilter((f) => ({ ...f, [k]: e.target.value })); };
  const pages = Math.max(1, Math.ceil((data.total ?? 0) / PAGE_SIZE));

  return (
    <div className="p-6 flex flex-col gap-4">
      {summary && (
        <div className="flex flex-wrap gap-3">
          <Stat label="미해결" value={summary.unresolvedTotal} accent={summary.unresolvedTotal > 0} />
          <Stat label="최근 24h 신규" value={summary.last24h} />
          {summary.bySource?.slice(0, 4).map((s) => (
            <Stat key={`${s.category}-${s.source}-${s.severity}`} label={`${s.source} ${s.severity}`} value={s.count} />
          ))}
          {summary.byChannel?.slice(0, 3).map((c) => (
            <div key={c.channelId ?? "none"} className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
              <span className="text-gray-500">채널 {c.channelId ?? "-"}</span><span className="font-semibold">{c.count}건</span>
              {c.channelId != null && (
                <button className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50" disabled={busyId === `bulk-${c.channelId}`}
                        onClick={() => onBulkResend(c.channelId, true)}>선물 일괄 재발송 미리보기</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 사고 행이 없어도 쓸 수 있는 도구 — 사고 기록은 배포 이후만 쌓이므로, 그 전의 실패는 여기서 가져오거나 채널 id 로 직접 다룬다(2026-11-03) */}
      <div className="flex flex-wrap items-end gap-4 rounded border border-dashed bg-white p-3 text-sm">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">채널 선물 재발송 도구 (사고 행과 무관)</span>
          <div className="flex items-center gap-2">
            <input className="w-28 rounded border px-2 py-1" placeholder="채널 id" value={toolChannel} onChange={(e) => setToolChannel(e.target.value)} />
            <button className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40" disabled={!toolChannel || busyId != null}
                    onClick={() => onBulkResend(Number(toolChannel), true)}>일괄 재발송 미리보기</button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">과거 선물 실패를 사고 기록으로 가져오기 (배포 이전 건)</span>
          <div className="flex items-center gap-2">
            <input type="date" className="rounded border px-2 py-1" value={backfillSince} onChange={(e) => setBackfillSince(e.target.value)} />
            <button className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40" disabled={busyId != null} onClick={() => onBackfill(true)}>건수 확인</button>
            <button className="rounded bg-gray-800 px-2 py-1 text-white disabled:opacity-40" disabled={busyId != null || !backfill || backfill.dryRun !== true || backfill.inserted === 0}
                    onClick={() => onBackfill(false)}>가져오기 실행</button>
            {backfill && (
              <span className="text-xs text-gray-600">
                {backfill.dryRun ? "예상" : "완료"}: 대상 {backfill.candidates} · {backfill.dryRun ? "가져올" : "가져온"} {backfill.inserted} · 이미 전달됨 {backfill.skippedDelivered} · 이미 있음 {backfill.skippedExisting}
              </span>
            )}
          </div>
        </div>
      </div>

      {bulk && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">채널 {bulk.channelId} 일괄 재발송 {bulk.result.dryRun ? "미리보기" : "결과"}</div>
          <div>대상 {bulk.result.candidates?.length ?? 0}건 · 예상 과금 {Number(bulk.result.expectedCharge ?? 0).toLocaleString()}원
            {bulk.result.skippedResolved > 0 && ` · 처리 완료로 제외 ${bulk.result.skippedResolved}건`}
            {!bulk.result.dryRun && ` · 성공 ${bulk.result.sent} · 실패 ${bulk.result.failed} · 거부 ${bulk.result.rejected?.length ?? 0}`}</div>
          {bulk.result.rejected?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-gray-600">{bulk.result.rejected.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          <div className="mt-2 flex gap-2">
            {bulk.result.dryRun && (
              <button className="rounded bg-red-600 px-3 py-1 text-white disabled:opacity-50" disabled={busyId != null || (bulk.result.candidates?.length ?? 0) === 0}
                      onClick={() => onBulkResend(bulk.channelId, false)}>실제 재발송 실행</button>
            )}
            <button className="rounded border px-3 py-1" onClick={() => setBulk(null)}>닫기</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded border bg-white p-3 text-sm">
        <label className="flex items-center gap-1">종류
          <select className="rounded border px-2 py-1" value={filter.source} onChange={set("source")}>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">심각도
          <select className="rounded border px-2 py-1" value={filter.severity} onChange={set("severity")}>
            <option value="">전체</option><option value="ERROR">ERROR</option><option value="WARN">WARN</option><option value="INFO">INFO</option>
          </select>
        </label>
        <label className="flex items-center gap-1">채널 id
          <input className="w-24 rounded border px-2 py-1" value={filter.channelId} onChange={set("channelId")} placeholder="예: 497" />
        </label>
        <label className="flex items-center gap-1">기간
          <input type="date" className="rounded border px-2 py-1" value={filter.from} onChange={set("from")} />
          <span>~</span>
          <input type="date" className="rounded border px-2 py-1" value={filter.to} onChange={set("to")} />
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={filter.unresolvedOnly} onChange={(e) => { setPage(0); setFilter((f) => ({ ...f, unresolvedOnly: e.target.checked })); }} />
          미해결만
        </label>
        <span className="ml-auto text-gray-500">총 {Number(data.total ?? 0).toLocaleString()}건</span>
      </div>

      {(loading || error) ? <LoadingBlock error={error} /> : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2">#</th><th className="px-3 py-2">발생</th><th className="px-3 py-2">심각도</th>
                <th className="px-3 py-2">종류 / 흐름</th><th className="px-3 py-2">사유</th><th className="px-3 py-2">채널</th>
                <th className="px-3 py-2">참조</th><th className="px-3 py-2">내용</th><th className="px-3 py-2">처리</th>
              </tr>
            </thead>
            <tbody>
              {data.rows?.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">해당 조건의 사고가 없습니다.</td></tr>}
              {data.rows?.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="px-3 py-2 text-gray-400">{r.id}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(r.occurredAt)}</td>
                  <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs ${SEVERITY_CLASS[r.severity] ?? ""}`}>{r.severity}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.source}<div className="text-xs text-gray-400">{r.flow ?? "-"}</div></td>
                  <td className="px-3 py-2 font-mono text-xs">{r.reasonCode ?? "-"}</td>
                  <td className="px-3 py-2">{r.channelId ?? "-"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.refType ? `${r.refType}/${r.refId}` : "-"}
                    {r.refs && <div className="text-gray-400">{Object.entries(r.refs).map(([k, v]) => `${k}=${v}`).join(" ")}</div>}
                  </td>
                  <td className="px-3 py-2 max-w-md break-all text-xs text-gray-700">{r.message}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.resolvedAt ? (
                      <span className="text-xs text-gray-500">완료 {fmt(r.resolvedAt)}<br/>{r.resolvedBy}{r.note ? ` · ${r.note}` : ""}</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {r.source === "KAKAO_GIFT" && r.refs?.historyId && (
                          <button className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50" disabled={busyId === r.id} onClick={() => onResendOne(r)}>재발송</button>
                        )}
                        {r.source === "KAKAO_GIFT" && r.channelId != null && (
                          <button className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50" disabled={busyId != null} onClick={() => onBulkResend(r.channelId, true)}>채널 일괄 미리보기</button>
                        )}
                        <button className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50" disabled={busyId === r.id} onClick={() => onResolve(r)}>처리 완료</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-center gap-3 border-t p-3 text-sm">
            <button className="rounded border px-2 py-0.5 disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>이전</button>
            <span>{page + 1} / {pages}</span>
            <button className="rounded border px-2 py-0.5 disabled:opacity-40" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>다음</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded border px-3 py-2 text-sm ${accent ? "border-red-300 bg-red-50" : "bg-white"}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-semibold ${accent ? "text-red-700" : ""}`}>{Number(value ?? 0).toLocaleString()}</div>
  </div>
);

export default IncidentAdmin;
